import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { useAudit, useAuditVerify } from "@/api/queries/audit";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { relativeTime } from "@/lib/relativeTime";

const PAGE_SIZE = 50;
const inputCls =
  "h-9 rounded-lg border border-border-strong bg-surface px-2.5 text-[13px] text-text outline-none placeholder:text-text3 focus:border-accent-border";

export function AuditList() {
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [target, setTarget] = useState("");
  const [outcome, setOutcome] = useState("");
  const [offset, setOffset] = useState(0);

  const filter = {
    action: action.trim() || undefined,
    actor: actor.trim() || undefined,
    target: target.trim() || undefined,
    outcome: outcome || undefined,
    limit: PAGE_SIZE,
    offset,
  };
  const { data, isLoading, isError, error, refetch } = useAudit(filter);
  const verify = useAuditVerify();
  const rows = data?.events ?? [];
  const total = data?.total ?? 0;

  function onChange(setter: (v: string) => void) {
    return (v: string) => { setter(v); setOffset(0); };
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[640px] text-[13px] text-text3">
          Manipulationssicherer Audit-Trail (wer/was/wann) — Config-/Enablement-/Admin-Vorgänge,
          append-only mit Integritäts-Hash-Kette.
        </p>
        {/* Integritäts-Status der Hash-Kette */}
        {verify.data && (
          verify.data.ok ? (
            <Badge variant="active" data-testid="audit-integrity">
              <ShieldCheck className="h-[14px] w-[14px]" /> Kette intakt ({verify.data.count})
            </Badge>
          ) : (
            <Badge variant="destructive" data-testid="audit-integrity">
              <ShieldAlert className="h-[14px] w-[14px]" /> Manipulation bei seq {verify.data.brokenAtSeq}
            </Badge>
          )
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="audit-filters">
        <input placeholder="Action (z.B. agent.create)" value={action} onChange={(e) => onChange(setAction)(e.target.value)} className={`${inputCls} min-w-[180px] flex-1`} data-testid="filter-action" />
        <input placeholder="Actor (User-/Admin-ID)" value={actor} onChange={(e) => onChange(setActor)(e.target.value)} className={`${inputCls} w-40`} data-testid="filter-actor" />
        <input placeholder="Target (Agent-/Connector-ID)" value={target} onChange={(e) => onChange(setTarget)(e.target.value)} className={`${inputCls} w-44`} data-testid="filter-target" />
        <select value={outcome} onChange={(e) => { setOutcome(e.target.value); setOffset(0); }} className={`${inputCls} w-36`} data-testid="filter-outcome">
          <option value="">Ergebnis: alle</option>
          <option value="success">Erfolg</option>
          <option value="failure">Fehlschlag</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => { setAction(""); setActor(""); setTarget(""); setOutcome(""); setOffset(0); }}>Zurücksetzen</Button>
        <Button variant="outline" size="sm" onClick={() => { refetch(); verify.refetch(); }}><RefreshCw className="h-4 w-4" />Aktualisieren</Button>
      </div>

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && rows.length === 0}
        emptyMessage="Keine Audit-Einträge (für diesen Filter)"
        onRetry={() => refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14 text-right">#</TableHead>
              <TableHead className="text-right">Zeit</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Ergebnis</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e.seq}>
                <TableCell className="text-right"><span className="mono tnum text-[11px] text-text3">{e.seq}</span></TableCell>
                <TableCell className="text-right"><span className="text-[12px] text-text3" title={e.ts}>{relativeTime(e.ts)}</span></TableCell>
                <TableCell>
                  {e.actor ? <span className="text-[12.5px] text-text2">{e.actor}</span> : <span className="text-text3">system</span>}
                  {e.actorType && <span className="ml-1.5 text-[10.5px] uppercase tracking-wide text-text3">{e.actorType}</span>}
                </TableCell>
                <TableCell><Badge variant="enrolled">{e.action}</Badge></TableCell>
                <TableCell>
                  {e.outcome === "success" ? (
                    <Badge variant="active" data-testid="audit-outcome">Erfolg</Badge>
                  ) : e.outcome === "failure" ? (
                    <Badge variant="destructive" data-testid="audit-outcome">Fehlschlag</Badge>
                  ) : (
                    <span className="text-text3">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {e.target ? (
                    /^[a-z0-9][a-z0-9._-]*$/.test(e.target)
                      ? <Link to={`/agents/${e.target}`} className="mono text-[11.5px] text-accent-text hover:underline">{e.target}</Link>
                      : <span className="mono text-[11.5px] text-text3">{e.target}</span>
                  ) : <span className="text-text3">—</span>}
                  {e.targetType && <span className="ml-1.5 text-[10.5px] uppercase tracking-wide text-text3">{e.targetType}</span>}
                </TableCell>
                <TableCell className="max-w-[320px] truncate" title={`${JSON.stringify(e.details)}${e.traceId ? `\ntrace: ${e.traceId}` : ""}${e.requestId ? `\nreq: ${e.requestId}` : ""}`}>
                  <span className="mono text-[11px] text-text3">{JSON.stringify(e.details)}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>

      <div className="flex items-center justify-between text-[12.5px] text-text3">
        <span data-testid="audit-total">{total} gesamt{total > 0 ? ` · zeige ${offset + 1}–${Math.min(offset + PAGE_SIZE, total)}` : ""}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Zurück</Button>
          <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Weiter</Button>
        </div>
      </div>
    </div>
  );
}
